import { useMemo } from 'react'
import { bindActionCreators } from 'redux'
import { useDispatch, useSelector } from 'react-redux'
import { useCart } from '@chordcommerce/gatsby-theme-autonomy'
import * as actions from '../../redux/actions/gift-card-details'
import * as selectors from '../../redux/selectors/gift-card-details'

export function useGiftCards() {
  const { addToCart, cart, modifyGiftCards, modifyLineItem, removeFromCart } =
    useCart()
  const dispatch = useDispatch()
  const giftCardDetails = useSelector(selectors.getGiftCardDetails)

  const addGiftCardDetails = useMemo(
    () => bindActionCreators(actions.addDetails, dispatch),
    [dispatch]
  )

  const removeGiftCardDetails = useMemo(
    () => bindActionCreators(actions.removeDetails, dispatch),
    [dispatch]
  )

  // Adds gift cards to the cart with recipient/gift details.
  const addGiftCardToCart = async (sku, quantity, cardDetails) => {
    try {
      await addGiftCardDetails(cardDetails)
      await addToCart({ lineItem: { sku, quantity } })
    } catch (error) {
      throw error
    }
  }

  // Applies gift card details to the gift card already in the cart.
  //
  // Necessary because the API doesn't allow adding the details while adding the
  // card to the cart. This must come after.
  const applyGiftCardDetails = async (id) => {
    try {
      if (giftCardDetails) {
        await modifyGiftCards({
          giftCards: {
            giftCardsDetails: [
              {
                id,
                ...giftCardDetails,
              },
            ],
          },
        }).then(async () => {
          await removeGiftCardDetails()
        })
      }
    } catch (error) {
      throw error
    }
  }

  // Removes a specific gift card from the cart.
  //
  // The API doesn't currently allow a specific gift card to be removed, so the
  // details for the remaining gift cards are shifted up before the last one is
  // removed via removeFromCart/modifyQuantity.
  const removeGiftCardFromCart = async (lineItemId, giftCardId) => {
    try {
      let giftCardsDetails = []
      cart.lineItems.forEach((lineItem) => {
        if (lineItem.id === lineItemId && lineItem.giftCards) {
          const giftCardIds = lineItem.giftCards.map(({ id }) => id)
          lineItem.giftCards.forEach((giftCard) => {
            if (giftCard.id !== giftCardId) {
              giftCardsDetails.push({
                ...giftCard,
                id: giftCardIds[giftCardsDetails.length],
              })
            }
          })
        }
      })

      const giftCardCount = giftCardsDetails.length
      if (giftCardCount === 0) {
        await removeFromCart({ lineItemId })
      } else {
        await modifyGiftCards({ giftCards: { giftCardsDetails } })
        await modifyLineItem({
          lineItemId,
          attributes: { quantity: giftCardCount },
        })
      }
    } catch (error) {
      throw error
    }
  }

  return {
    addGiftCardToCart,
    applyGiftCardDetails,
    removeGiftCardFromCart,
  }
}
